import React from "react";
import { Helmet } from "react-helmet";
import HomepageHero from "@webcore/components/domain/content-site/sections/HomepageHero";
import LogosStrip from "@webcore/components/domain/content-site/sections/LogosStrip";
import ThesisSection from "@webcore/components/domain/content-site/sections/ThesisSection";
import PrinciplesSection, { Principle } from "@webcore/components/domain/content-site/sections/PrinciplesSection";
import WorkflowsSection, { WorkflowCardData } from "@webcore/components/domain/content-site/sections/WorkflowsSection";
import ArchitectureSection from "@webcore/components/domain/content-site/sections/ArchitectureSection";
import CalloutSection from "@webcore/components/domain/content-site/sections/CalloutSection";
import CtaSection from "@webcore/components/domain/content-site/sections/CtaSection";
import QuoteSection from "@webcore/components/domain/content-site/sections/QuoteSection";
import FaqSection from "@webcore/components/domain/content-site/sections/FaqSection";
import { homepageFaqs } from "@/content/faqs";

import pipelineReviewImg from "@/assets/workflows/pipeline-review.jpg";
import mrrCloseImg from "@/assets/workflows/mrr-close.jpg";
import commissionRunImg from "@/assets/workflows/commission-run.jpg";
import worstSpreadsheetImg from "@/assets/workflows/worst-spreadsheet.jpg";

const connectorLogos = [
  { label: "Maxio" },
  { label: "Salesforce", style: "serif" as const },
  { label: "HubSpot", style: "italic" as const },
  { label: "Stripe" },
  { label: "Shopify", style: "mono" as const },
  { label: "Amazon" },
  { label: "NetSuite", style: "italic" as const },
  { label: "QuickBooks" },
];

const principles: Principle[] = [
  {
    num: "01",
    kicker: "unify",
    title: <>One source of truth across <em>Salesforce, HubSpot, Stripe, and NetSuite.</em></>,
    body: (
      <>
        Billing, CRM, accounting, payments, ecommerce. Surfaced as{" "}
        <strong>typed business objects</strong> — customers, deals, subscriptions, revenue events,
        monthly rollforwards — that your team can query directly. The work you'd otherwise piece
        together with Fivetran, dbt, and a BI tool, replaced with one platform.
      </>
    ),
  },
  {
    num: "02",
    kicker: "defend",
    title: <>Every number traces to a <em>source record.</em></>,
    body: (
      <>
        Every transformation is <strong>versioned SQL you can read, review, and change</strong>.
        Every override leaves a note. When finance asks "where did this number come from?" — the
        answer is two clicks away, not a three-day reconciliation project.
      </>
    ),
  },
  {
    num: "03",
    kicker: "run",
    title: <>Purpose-built workflows for the jobs that <em>consume your team's time.</em></>,
    body: (
      <>
        Pipeline review, MRR close, commission calculation, variance reporting, exception triage.{" "}
        <strong>Automated where automation is safe, surfaced for human judgment where it isn't</strong>,
        and built on the same data model your dashboards run on. The numbers in the workflow are
        the same numbers in the report.
      </>
    ),
  },
];

const workflows: WorkflowCardData[] = [
  {
    num: "// 01",
    title: "Pipeline Review",
    sub: "Stop running forecast meetings on stale Salesforce reports.",
    body: (
      <>
        Weighted pipeline by stage and product. <strong>Zombie deal detection</strong> — stale, $0,
        or no-recent-activity deals flagged automatically. Forecast vs. actual accuracy tracked at
        the rep and team level. Drill from any tile into the underlying opportunities, owners, and
        source records.
      </>
    ),
    screenshot: pipelineReviewImg,
    replacesLabel: "Replaces or sits alongside",
    replacesItems: "Salesforce reports · Clari · forecast spreadsheets",
  },
  {
    num: "// 02",
    title: "MRR Close",
    sub: "The monthly rollforward your finance team rebuilds in a spreadsheet — built once, run forever.",
    body: (
      <>
        Monthly MRR movement classified into new logo, cross-sell, upsell, downgrade, and churn.{" "}
        <strong>Customer-level audit panel</strong> for every cell. Override workflow for the
        judgment calls that don't fit the rules. Projections from contracted future revenue when
        actuals haven't billed yet.
      </>
    ),
    screenshot: mrrCloseImg,
    replacesLabel: "Replaces",
    replacesItems: "the golden-truth spreadsheet · the analyst who maintains it · the three-day month-end cycle",
  },
  {
    num: "// 03",
    title: "Commission Run",
    sub: "Commission rules as versioned code, not a comp spreadsheet nobody trusts.",
    body: (
      <>
        Per-rep monthly statements. New deals, prior-month deals with current MRR deltas applied to
        rates, terminated deals zeroed automatically. <strong>Bonus tiers, accelerators, and
        clawback rules live as readable SQL</strong> — not buried in a vendor's UI.
      </>
    ),
    screenshot: commissionRunImg,
    replacesLabel: "Replaces",
    replacesItems: "CaptivateIQ · QuotaPath · your homegrown comp sheet · the comp disputes",
  },
  {
    num: "// 04",
    title: "Your Most Complicated Custom Report",
    sub: "(And your worst spreadsheet.) Simplified.",
    body: (
      <>
        Every finance leader knows that report — the one that stitches together{" "}
        <strong>Salesforce, Stripe, and NetSuite</strong>, takes three days to pull, and still
        gets challenged in the board meeting. DataFabrIQ builds it into your governed data model
        so it <strong>runs automatically</strong>, delivers the same numbers every time, and
        drills down to the <strong>source record behind every cell</strong>.
      </>
    ),
    screenshot: worstSpreadsheetImg,
    screenshotAlt: "The worst spreadsheet — manual inputs, VLOOKUPs, a plug, and a total that hasn't tied since March",
    replacesLabel: "Replaces",
    replacesItems: "Rev_MASTER_v3_FINAL_useThisOne (recovered).xlsx · the plug that ties it to the bank",
  },
];

const architectureSources = [
  "Maxio", "Salesforce", "HubSpot", "Stripe", "Shopify", "Amazon", "NetSuite", "QuickBooks",
];

const Homepage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>DataFabrIQ — One Source of Truth for Finance & GTM Data</title>
        <meta
          name="description"
          content="DataFabrIQ unifies Salesforce, HubSpot, Stripe & NetSuite into one trusted, auditable source of truth your team and your AI can build on. No data team required."
        />
        <link rel="canonical" href="https://datafabriq.co/" />
      </Helmet>

      <HomepageHero
        heading={
          <>
            One auditable source of truth<br />
            <span className="dim">for Finance &amp; GTM data</span>
            <span className="cs-hero-tagline accent">— built for AI.</span>
          </>
        }
        lede={
          <>
            DataFabrIQ unifies <strong>Salesforce, HubSpot, Stripe, and NetSuite</strong> into one
            clean, trusted, auditable version of your numbers — the data foundation your
            dashboards, workflows, and AI all read from.
            <br />
            <br />
            <strong>Quit running your business on spreadsheets.</strong> DataFabrIQ is the
            operational data platform you'd build if you had a data team. Already built. No data
            team required.
          </>
        }
        primaryLabel="Talk to Sales →"
      />

      <LogosStrip entries={connectorLogos} />

      <ThesisSection
        id="platform"
        label="// our thesis"
        heading={
          <>
            Why does AI fail to deliver the promised ROI?<br />
            Because it's built on <span className="accent">messy, siloed data.</span>
          </>
        }
        body={
          <>
            <p>
              AI workflows and reports built on disconnected, siloed data{" "}
              <strong>break the moment they leave the chat window</strong>. The numbers underneath
              them are the output of dozens of decisions — which records to include, how to
              classify a movement, what to do when Maxio and Salesforce disagree, whose rep ID
              gets the credit. Most tools bury those decisions in vendor UI, and AI built on top
              inherits every inconsistency.
            </p>
            <p>
              DataFabrIQ gives your AI a <strong>governed data foundation</strong> and framework
              to build on — business logic as SQL you can read and version, every cell traced
              back to a source record — so solutions hold up in production and{" "}
              <strong>deliver ROI across the whole company</strong>, not just in the demo.
            </p>
          </>
        }
      />

      <PrinciplesSection
        eyebrow="// what it does"
        heading={
          <>
            How do you know you can trust the number?<br />
            Full <span className="accent">data lineage</span> on every line.
          </>
        }
        intro={
          <>
            Traditional BI tools ask you to take the numbers on faith. DataFabrIQ traces every
            number back to its source record, so finance, sales, and engineering all trust the
            same data — governed, defensible, and secure. Used across the entire company, with no
            data team to hire.
          </>
        }
        principles={principles}
      />

      <WorkflowsSection
        id="workflows"
        eyebrow="// what your team stops doing in spreadsheets"
        heading={
          <>
            The operational software you'd build if you had a data team.{" "}
            <span className="serif">Already built.</span>
          </>
        }
        intro={
          <>
            Every workflow runs on the same auditable data model. Every number is drillable. Every
            override leaves a trail. These aren't dashboards with extra buttons — they're the apps
            your team actually opens to get the work done.
          </>
        }
        workflows={workflows}
      />

      <ArchitectureSection
        id="how-it-works"
        eyebrow="// how it works"
        heading={
          <>
            One platform.<br />
            Two surfaces.<br />
            <span className="serif">A foundation you can audit.</span>
          </>
        }
        intro={
          <>
            Built on a medallion architecture: source systems land in a bronze layer of raw,
            untouched data; transformation specs — dedupe, merge, optimize and clean, written by
            your team or ours — refine it through silver into a typed gold model of business
            objects. KPIs, dashboards, and AI workflows all read from that same model, so the
            number in a Monday pipeline review matches the number on the quarterly board deck.
            Every cell knows where it came from.
          </>
        }
        sources={architectureSources}
        foot="Every transformation versioned · every cell drillable · every override traceable"
      />

      <CalloutSection
        quote={
          <>
            The dashboard is the <em>last mile.</em> The product is everything{" "}
            <em>underneath it</em> — and the software you build on top of it when a chart is not
            enough.
          </>
        }
        cite="— Data FabrIQ engineering principle · №01"
      />

      <ThesisSection
        label="// ai strategy"
        heading={
          <>
            Get your AI strategy right the first time —<br />
            and right <span className="accent">at scale.</span>
          </>
        }
        body={
          <>
            <p>
              Anyone can vibe-code a prototype. Turning it into something your whole organization
              relies on is a different challenge. Enterprise-grade hosting, security, and
              auditability are necessary but not sufficient. DataFabrIQ is the{" "}
              <strong>AI expertise at your table</strong> — knowing exactly where and how AI
              should be applied so it holds up long past the demo.
            </p>
            <p>
              We evolve alongside the AI landscape, so your strategy is{" "}
              <strong>built right the first time</strong>, not rebuilt from scratch every six
              months.
            </p>
          </>
        }
      />

      <CtaSection
        heading={
          <>
            Board-level answers <span className="serif">on demand,</span><br />
            not after a three-day scramble.
          </>
        }
        body="Learn how you can resolve queries up to 90% faster with DataFabrIQ."
        primaryLabel="Book a Demo →"
      />

      <QuoteSection
        id="pricing"
        eyebrow="// pricing"
        heading={
          <>
            Contact Sales to get started
          </>
        }
        intro={
          <>
            <p>
              DataFabrIQ is offered as a platform subscription. Pricing is quote-based and
              depends on the number of source systems you connect, your data volume, and the size
              of your team. Connectors — including the Amazon Seller Central connector — are
              included in the subscription: <strong>no separate connector fees, usage surcharges,
              or hidden service fees.</strong>
            </p>
            <p>
              Tell us a little about your business and our team will follow up with a detailed
              quote, usually within one business day.
            </p>
          </>
        }
      />

      <FaqSection
        id="faq"
        eyebrow="// faq"
        heading={<>Questions, <span className="serif">answered.</span></>}
        faqs={homepageFaqs}
      />
    </>
  );
};

export default Homepage;
